// src/pages/dashboard/components/WorkQueueSummary.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox,
  ChevronRight,
  Clock,
  AlertCircle,
  PauseCircle,
  CheckCircle2,
  XCircle,
  PlusCircle,
  RefreshCw,
  Users,
  User,
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../hooks/useAuth';
import { getWorkQueueDashboardStats } from '../../../services/api/orderAssignments';
import styles from './WorkQueueSummary.module.css';

const WorkQueueSummary = ({ shopId }) => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();

  const canViewFullQueue = hasPermission('view_full_work_queue');

  const [view, setView] = useState(canViewFullQueue ? 'all' : 'assigned');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { view };
      if (shopId && shopId !== 'all') params.shopId = shopId;
      const response = await getWorkQueueDashboardStats(params);
      setStats(response.stats);
    } catch (err) {
      console.error('Error fetching work queue dashboard stats:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [shopId, view]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (!hasPermission('view_work_queue')) return null;

  const isAssigned = view === 'assigned';

  const queueCards = [
    {
      key: 'inQueue',
      label: 'In Queue',
      icon: Inbox,
      value: stats?.myQueue?.inQueue ?? 0,
      accent: 'blue',
    },
    {
      key: 'pending',
      label: 'Pending',
      icon: Clock,
      value: stats?.myQueue?.pending ?? 0,
      accent: 'amber',
    },
    {
      key: 'notConfirmed',
      label: 'Not Confirmed',
      icon: AlertCircle,
      value: stats?.myQueue?.notConfirmed ?? 0,
      accent: 'orange',
    },
    {
      key: 'hold',
      label: 'On Hold',
      icon: PauseCircle,
      value: stats?.myQueue?.hold ?? 0,
      accent: 'purple',
    },
  ];

  const todayCards = [
    {
      key: 'totalCreated',
      label: isAssigned ? 'Total Assigned' : 'Total Created',
      icon: PlusCircle,
      value: stats?.todaySummary?.totalCreated ?? 0,
      accent: 'teal',
    },
    {
      key: 'totalConfirmed',
      label: 'Total Confirmed',
      icon: CheckCircle2,
      value: stats?.todaySummary?.totalConfirmed ?? 0,
      accent: 'green',
    },
    {
      key: 'totalCancelled',
      label: 'Total Cancelled',
      icon: XCircle,
      value: stats?.todaySummary?.totalCancelled ?? 0,
      accent: 'red',
    },
  ];

  const queueSectionTitle = isAssigned ? 'My Queue' : 'Caller Queue';

  return (
    <div className={styles.wrapper}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Inbox size={20} className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Work Queue</h3>
        </div>
        <div className={styles.headerRight}>
          {canViewFullQueue && (
            <div className={styles.viewToggle}>
              <button
                className={`${styles.toggleBtn} ${view === 'all' ? styles.toggleActive : ''}`}
                onClick={() => setView('all')}
              >
                <Users size={13} />
                All
              </button>
              <button
                className={`${styles.toggleBtn} ${view === 'assigned' ? styles.toggleActive : ''}`}
                onClick={() => setView('assigned')}
              >
                <User size={13} />
                Assigned
              </button>
            </div>
          )}
          <button
            className={styles.refreshBtn}
            onClick={fetchStats}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? styles.spinning : ''} />
          </button>
          <Link to="/order-assignments/my-queue" className={styles.viewAllLink}>
            View All
            <ChevronRight size={15} />
          </Link>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────── */}
      {error ? (
        <div className={styles.errorState}>
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={fetchStats} className={styles.retryBtn}>Retry</button>
        </div>
      ) : (
        <div className={styles.body}>
          {/* My Queue / Caller Queue */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>{queueSectionTitle}</div>
            <div className={styles.queueGrid}>
              {queueCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.key}
                    className={`${styles.statCard} ${styles[`accent_${card.accent}`]} ${loading ? styles.cardLoading : ''}`}
                  >
                    <div className={styles.cardIconWrap}>
                      <Icon size={16} />
                    </div>
                    <div className={styles.cardValue}>
                      {loading ? <span className={styles.skeletonNum} /> : card.value.toLocaleString()}
                    </div>
                    <div className={styles.cardLabel}>{card.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className={styles.divider} />

          {/* Today Summary */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Today's Summary</div>
            <div className={styles.todayGrid}>
              {todayCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.key}
                    className={`${styles.todayCard} ${styles[`accent_${card.accent}`]} ${loading ? styles.cardLoading : ''}`}
                  >
                    <div className={styles.todayCardInner}>
                      <div className={styles.todayIconWrap}>
                        <Icon size={18} />
                      </div>
                      <div className={styles.todayInfo}>
                        <div className={styles.todayValue}>
                          {loading ? <span className={styles.skeletonNum} /> : card.value.toLocaleString()}
                        </div>
                        <div className={styles.todayLabel}>{card.label}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkQueueSummary;